import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import axios from 'axios';
import {
  Plus,
  Edit,
  Trash2,
  Upload,
  X,
  Image as ImageIcon,
  ArrowLeft,
  Save
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { uploadImage } from '../../lib/uploadImage';
import { invalidateCache } from '../../lib/apiCache';
import RichTextEditor from '../../components/RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyFormData = {
  name: '',
  nameRu: '',
  slug: '',
  slugRu: '',
  icon: '',
  image: '',
  parentId: null,
  seoTitle: '',
  seoTitleRu: '',
  seoDescription: '',
  seoDescriptionRu: '',
  seoKeywords: '',
  description: '',
  descriptionRu: '',
  faqs: []
};

const CYRILLIC_MAP = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh',
  з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
  п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts',
  ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya'
};

const slugifyRu = (text = '') => {
  if (!text) return '';
  const lower = text.toString().toLowerCase().trim();
  let out = '';
  for (const ch of lower) {
    if (CYRILLIC_MAP[ch] !== undefined) {
      out += CYRILLIC_MAP[ch];
    } else {
      out += ch;
    }
  }
  return out
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

const getImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('blob:') ||
    url.startsWith('data:')
  ) {
    return url;
  }

  return `${BACKEND_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

const CategoriesManagement = () => {
  const { getAuthHeaders } = useAdmin();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('ro');
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [faqs, setFaqs] = useState([]);
  const [slugRuEdited, setSlugRuEdited] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ă/g, 'a')
      .replace(/â/g, 'a')
      .replace(/î/g, 'i')
      .replace(/ș/g, 's')
      .replace(/ş/g, 's')
      .replace(/ț/g, 't')
      .replace(/ţ/g, 't')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca categoriile',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...emptyFormData, faqs: [] });
    setFaqs([]);
    setImagePreview(null);
    setActiveTab('ro');
    setSlugRuEdited(false);
  };

  const openCreateForm = (parentId = null) => {
    setEditingCategory(null);
    resetForm();
    setFormData({ ...emptyFormData, parentId, faqs: [] });
    setIsCategoryFormOpen(true);
  };

  const closeCategoryForm = () => {
    setIsCategoryFormOpen(false);
    setEditingCategory(null);
    resetForm();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      e.target.value = '';
      return;
    }

    try {
      const url = await uploadImage(file, getAuthHeaders());
      setImagePreview(url);
      setFormData((prev) => ({ ...prev, image: url, icon: url }));
    } catch (err) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut încărca imaginea',
        variant: 'destructive'
      });
    }

    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: 'Eroare',
        description: 'Numele categoriei este obligatoriu',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.image) {
      toast({
        title: 'Eroare',
        description: 'Te rog încarcă o imagine pentru categorie',
        variant: 'destructive'
      });
      return;
    }

    const categoryData = {
      ...formData,
      slug: generateSlug(formData.name),
      icon: formData.image,
      itemCount: editingCategory?.itemCount || 0,
      parentId: formData.parentId || null,
      faqs: faqs
        .filter((faq) => faq.question && faq.answer)
        .map((faq) => ({
          question: faq.question,
          answer: faq.answer,
          questionRu: faq.questionRu || '',
          answerRu: faq.answerRu || ''
        }))
    };

    try {
      if (editingCategory) {
        await axios.put(
          `${API}/categories/${editingCategory.id}`,
          categoryData,
          getAuthHeaders()
        );

        toast({
          title: 'Succes',
          description: 'Categoria a fost actualizată!'
        });
      } else {
        await axios.post(`${API}/categories`, categoryData, getAuthHeaders());

        toast({
          title: 'Succes',
          description: 'Categoria a fost creată!'
        });
      }

      closeCategoryForm();
      fetchCategories();
      invalidateCache('/api/categories');
    } catch (error) {
      toast({
        title: 'Eroare',
        description:
          error.response?.data?.detail || 'Nu s-a putut salva categoria',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriți să ștergeți această categorie?')) return;

    try {
      await axios.delete(`${API}/categories/${id}`, getAuthHeaders());
      toast({ title: 'Succes', description: 'Categoria a fost ștearsă!' });
      fetchCategories();
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut șterge categoria',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      nameRu: category.nameRu || '',
      slug: category.slug || '',
      slugRu: category.slugRu || '',
      icon: category.icon || '',
      image: category.image || category.icon || '',
      parentId: category.parentId || null,
      seoTitle: category.seoTitle || '',
      seoTitleRu: category.seoTitleRu || '',
      seoDescription: category.seoDescription || '',
      seoDescriptionRu: category.seoDescriptionRu || '',
      seoKeywords: category.seoKeywords || '',
      description: category.description || '',
      descriptionRu: category.descriptionRu || '',
      faqs: category.faqs || []
    });
    setFaqs(category.faqs || []);
    setImagePreview(category.image || category.icon || null);
    setActiveTab('ro');
    const autoFromName = slugifyRu(category.nameRu || '');
    setSlugRuEdited(!!category.slugRu && category.slugRu !== autoFromName);
    setIsCategoryFormOpen(true);
  };

  const addFaq = () => {
    setFaqs([
      ...faqs,
      { question: '', answer: '', questionRu: '', answerRu: '' }
    ]);
  };

  const updateFaq = (index, field, value) => {
    const nextFaqs = [...faqs];
    nextFaqs[index][field] = value;
    setFaqs(nextFaqs);
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const getDescendantIds = (parentId) => {
    const ids = new Set([parentId]);
    let added = true;

    while (added) {
      added = false;
      for (const c of categories) {
        if (c.parentId && ids.has(c.parentId) && !ids.has(c.id)) {
          ids.add(c.id);
          added = true;
        }
      }
    }

    return ids;
  };

  const getEligibleParents = () => {
    if (!editingCategory) return categories;
    const blocked = getDescendantIds(editingCategory.id);
    return categories.filter((c) => !blocked.has(c.id));
  };

  const getDepth = (catId) => {
    let depth = 0;
    let current = categories.find((c) => c.id === catId);

    while (current && current.parentId) {
      depth += 1;
      current = categories.find((c) => c.id === current.parentId);
    }

    return depth;
  };

  const getParentCategories = () => categories.filter((cat) => !cat.parentId);
  const getSubCategories = (parentId) =>
    categories.filter((cat) => cat.parentId === parentId);

  const renderCategoryRow = (cat, depth = 0) => {
    const subs = getSubCategories(cat.id);
    const indentPx = 16 + depth * 28;
    const parentName = cat.parentId
      ? categories.find((c) => c.id === cat.parentId)?.name || '-'
      : '-';
    const rowBg =
      depth === 0 ? 'bg-white' : depth === 1 ? 'bg-brand-50/60' : 'bg-brand-100/40';

    return (
      <React.Fragment key={cat.id}>
        <tr className={`${rowBg} hover:bg-brand-50 transition`}>
          <td className="px-6 py-3" style={{ paddingLeft: `${indentPx}px` }}>
            {cat.image || cat.icon ? (
              <img
                src={getImageUrl(cat.image || cat.icon)}
                alt={cat.name}
                className="w-10 h-10 object-cover rounded-lg shadow-md"
              />
            ) : (
              <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400">
                <ImageIcon className="w-4 h-4" />
              </div>
            )}
          </td>

          <td className="px-6 py-3">
            <div
              className={`${
                depth === 0 ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'
              }`}
            >
              {depth > 0 && (
                <span className="text-gray-400 mr-1">
                  {'↳ '.repeat(depth)}
                </span>
              )}
              {cat.name}
            </div>
          </td>

          <td className="px-6 py-3 text-gray-600">{cat.slug}</td>
          <td className="px-6 py-3 text-gray-500">{parentName}</td>

          <td className="px-6 py-3">
            <span className="bg-brand-100 text-brand-800 px-3 py-1 rounded-full text-sm font-semibold">
              {cat.itemCount || 0}
            </span>
          </td>

          <td className="px-6 py-3">
            <div className="flex gap-2 justify-end pr-2">
              <button
                onClick={() => openCreateForm(cat.id)}
                title="Adaugă subcategorie"
                data-testid={`add-sub-${cat.id}`}
                className="p-2.5 rounded-full bg-brand-50 text-brand-700 hover:bg-brand-100 transition"
              >
                <Plus className="w-4 h-4" />
              </button>

              <button
                onClick={() => handleEdit(cat)}
                data-testid={`edit-cat-${cat.id}`}
                title="Editează"
                className="p-2.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition"
              >
                <Edit className="w-4 h-4" />
              </button>

              <button
                onClick={() => handleDelete(cat.id)}
                data-testid={`delete-cat-${cat.id}`}
                title="Șterge"
                className="p-2.5 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100 transition"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </td>
        </tr>

        {subs.map((s) => renderCategoryRow(s, depth + 1))}
      </React.Fragment>
    );
  };

  if (loading && !isCategoryFormOpen) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-brand-600"></div>
      </div>
    );
  }

  if (isCategoryFormOpen) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={closeCategoryForm}
                className="w-11 h-11 rounded-full bg-gray-50 hover:bg-gray-100 text-gray-700 flex items-center justify-center transition"
                title="Înapoi"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              <div>
                <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">
                  {editingCategory ? 'Editează categorie' : 'Adaugă categorie nouă'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Slug-ul se generează automat din numele categoriei.
                </p>
              </div>
            </div>

            <button
              type="submit"
              form="category-form"
              className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-5 py-2.5 rounded-full font-semibold shadow-md shadow-brand-200 transition"
            >
              <Save className="w-4 h-4" />
              {editingCategory ? 'Actualizează categoria' : 'Salvează categoria'}
            </button>
          </div>
        </div>

        <form
          id="category-form"
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden"
        >
          <div className="border-b border-gray-100 px-6 pt-5">
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'ro', label: 'RO' },
                { id: 'ru', label: 'RU' },
                { id: 'seo', label: 'SEO' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-3 rounded-t-2xl text-sm font-bold transition ${
                    activeTab === tab.id
                      ? 'bg-brand-500 text-white shadow-md shadow-brand-100'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6 space-y-6">
            {activeTab === 'ro' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Imagine Categorie *
                  </label>

                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-brand-500 transition">
                    {imagePreview ? (
                      <div className="relative inline-block">
                        <img
                          src={getImageUrl(imagePreview)}
                          alt="Preview"
                          className="max-h-40 rounded-lg shadow-lg"
                        />

                        <button
                          type="button"
                          onClick={() => {
                            setImagePreview(null);
                            setFormData({ ...formData, image: '', icon: '' });
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition shadow-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-3" />
                        <label className="cursor-pointer">
                          <span className="bg-brand-500 text-white px-6 py-3 rounded-lg inline-flex items-center gap-2 hover:bg-brand-600 transition font-semibold">
                            <Upload className="w-5 h-5" />
                            Încarcă Imagine
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </label>
                        <p className="text-xs text-gray-500 mt-3">
                          PNG, JPG, WebP până la limita setată în backend.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Nume Categorie *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="ex: Haine Femei"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Categorie Părinte
                  </label>
                  <select
                    data-testid="parent-category-select"
                    value={formData.parentId || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        parentId: e.target.value || null
                      })
                    }
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                  >
                    <option value="">Nicio categorie părinte</option>
                    {getEligibleParents().map((cat) => {
                      const depth = getDepth(cat.id);
                      const indent = '— '.repeat(depth);
                      return (
                        <option key={cat.id} value={cat.id}>
                          {indent}
                          {cat.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            )}

            {activeTab === 'ru' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Nume Categorie RU
                  </label>
                  <input
                    type="text"
                    value={formData.nameRu}
                    onChange={(e) => {
                      const nameRu = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        nameRu,
                        slugRu: slugRuEdited ? prev.slugRu : slugifyRu(nameRu),
                      }));
                    }}
                    placeholder="напр: Женская одежда"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Slug URL (RU)
                  </label>
                  <input
                    type="text"
                    value={formData.slugRu}
                    onChange={(e) => {
                      setSlugRuEdited(true);
                      setFormData({ ...formData, slugRu: e.target.value });
                    }}
                    placeholder="напр: zhenskaya-odezhda"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                  />
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-xs text-gray-500">
                      Obligatoriu de pus pentru link RU
                    </p>
                    {slugRuEdited && formData.nameRu && (
                      <button
                        type="button"
                        onClick={() => {
                          setSlugRuEdited(false);
                          setFormData((prev) => ({
                            ...prev,
                            slugRu: slugifyRu(prev.nameRu),
                          }));
                        }}
                        className="text-xs font-semibold text-brand-600 hover:underline"
                      >
                        Regenerează din nume
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      SEO Title RO
                    </label>
                    <input
                      type="text"
                      value={formData.seoTitle}
                      onChange={(e) =>
                        setFormData({ ...formData, seoTitle: e.target.value })
                      }
                      placeholder="Titlu pentru Google"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      SEO Title RU
                    </label>
                    <input
                      type="text"
                      value={formData.seoTitleRu}
                      onChange={(e) =>
                        setFormData({ ...formData, seoTitleRu: e.target.value })
                      }
                      placeholder="SEO заголовок"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Meta Description RO
                    </label>
                    <textarea
                      rows="4"
                      value={formData.seoDescription}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          seoDescription: e.target.value
                        })
                      }
                      placeholder="Descriere scurtă pentru motoarele de căutare"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Meta Description RU
                    </label>
                    <textarea
                      rows="4"
                      value={formData.seoDescriptionRu}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          seoDescriptionRu: e.target.value
                        })
                      }
                      placeholder="Краткое SEO описание"
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    SEO Keywords
                  </label>
                  <input
                    type="text"
                    value={formData.seoKeywords}
                    onChange={(e) =>
                      setFormData({ ...formData, seoKeywords: e.target.value })
                    }
                    placeholder="ex: scule, echipamente, Moldova"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                  />
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-extrabold text-gray-900 mb-4">
                    Descriere categorie
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Descriere RO
                      </label>
                      <RichTextEditor
                        value={formData.description}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            description: value
                          })
                        }
                        placeholder="Text care se va afișa jos pe pagina categoriei"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Descriere RU
                      </label>
                      <RichTextEditor
                        value={formData.descriptionRu}
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            descriptionRu: value
                          })
                        }
                        placeholder="Текст для страницы категории"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-extrabold text-gray-900">
                      FAQ categorie
                    </h3>

                    <button
                      type="button"
                      onClick={addFaq}
                      className="text-brand-600 hover:text-brand-700 font-semibold text-sm flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Adaugă întrebare
                    </button>
                  </div>

                  {faqs.length > 0 ? (
                    <div className="space-y-3">
                      {faqs.map((faq, index) => (
                        <div
                          key={index}
                          className="rounded-2xl border border-gray-200 bg-gray-50 p-3 space-y-3"
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                              type="text"
                              placeholder="Întrebare RO"
                              value={faq.question}
                              onChange={(e) =>
                                updateFaq(index, 'question', e.target.value)
                              }
                              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200"
                            />

                            <input
                              type="text"
                              placeholder="Întrebare RU"
                              value={faq.questionRu || ''}
                              onChange={(e) =>
                                updateFaq(index, 'questionRu', e.target.value)
                              }
                              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <textarea
                              rows="3"
                              placeholder="Răspuns RO"
                              value={faq.answer}
                              onChange={(e) =>
                                updateFaq(index, 'answer', e.target.value)
                              }
                              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200"
                            />

                            <textarea
                              rows="3"
                              placeholder="Răspuns RU"
                              value={faq.answerRu || ''}
                              onChange={(e) =>
                                updateFaq(index, 'answerRu', e.target.value)
                              }
                              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-200"
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => removeFaq(index)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg transition"
                          >
                            <X className="w-4 h-4" />
                            Șterge întrebarea
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Nicio întrebare adăugată
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 bg-gray-50/60 px-6 py-4 flex flex-wrap gap-3 justify-end">
            <button
              type="button"
              onClick={closeCategoryForm}
              className="px-5 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition font-semibold"
            >
              Anulează
            </button>

            <button
              type="submit"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-500 text-white rounded-xl hover:bg-brand-600 transition font-semibold shadow-md shadow-brand-200"
            >
              <Save className="w-4 h-4" />
              {editingCategory ? 'Actualizează' : 'Creează'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm text-gray-900">
        <div className="flex justify-between items-center gap-4">
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">
              Gestionare Categorii
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Total: {categories.length} categorii
            </p>
          </div>

          <button
            onClick={() => openCreateForm()}
            className="inline-flex items-center gap-2 bg-brand-500 hover:bg-brand-600 text-white px-5 py-2.5 rounded-full font-semibold shadow-md shadow-brand-200 transition"
          >
            <Plus className="w-5 h-5" />
            Adaugă Categorie
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/60 text-gray-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4 text-left font-semibold">Icon/Imagine</th>
                <th className="px-6 py-4 text-left font-semibold">
                  Nume Categorie
                </th>
                <th className="px-6 py-4 text-left font-semibold">Slug</th>
                <th className="px-6 py-4 text-left font-semibold">
                  Categorie Părinte
                </th>
                <th className="px-6 py-4 text-left font-semibold">Produse</th>
                <th className="px-6 py-4 text-right font-semibold pr-8">
                  Acțiuni
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {getParentCategories().map((category) =>
                renderCategoryRow(category, 0)
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CategoriesManagement;